import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography
} from "@mui/material";
import { useFormik } from "formik";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { FormEvent, useEffect, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { CardStatus, Employee } from "../../models";
import { EmployeeStatus } from "../../models/employees/employeeStatus";
import { TagReference } from "../../models/tagReference";
import { useConfig } from "../../utils/useConfig";
import { useData } from "../../utils/useData";
import { useDynamicEmployeeValidationSchema } from "../../utils/useDynamicValidationSchema";
import { Card } from "../shared/Card";
import { PlanSelection } from "../shared/PlanSelection";
import { TagSelection } from "../shared/TagSelection";
import { PersonalDetailsForm } from "./PersonalDetailsForm";

type EditUserProps = {
  open: boolean;
  handleClose: () => void;
  employee: Employee;
};

type FormValuesType = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  // address1: string;
  // address2: string;
  // address3: string;
  // address4: string;
  // address5: string;
  // city: string;
  // stateOrProvince: string;
  // postalCode: string;
  // country: string;
};

const DEFAULT_VALUE = "-1";

export const EditUser = ({ open, handleClose, employee }: EditUserProps) => {
  const { config } = useConfig();
  const { userInfoData, employerId, allEmployees, employees, employer } =
    useData();
  const { getToken } = useKindeAuth();

  const [canUpdateStatus, setCanUpdateStatus] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState<
    { planId: string; tierId: string }[]
  >(
    employee.accounts.map((account) => {
      return { planId: account.planId, tierId: account.tierId };
    })
  );
  const [tags, setTags] = useState<TagReference[]>(
    employee.tags.map((tag) => {
      return { tagId: tag.id, tagCategoryId: tag.tagCategoryId };
    })
  );

  const [status] = useState<EmployeeStatus>(employee.status);
  const [error, setError] = useState("");

  const mutationUpdateUser = useMutation(
    async (formValues: FormValuesType) => {
      const countryCode: any = employer?.data?.country?.id;

      let formattedPhoneNumber = null;
      if (formValues.phone?.length) {
        formattedPhoneNumber = parsePhoneNumberFromString(
          formValues.phone,
          countryCode
        )?.number;
      }

      const response = await fetch(
        `${config?.API_URL}/employers/${employerId}/employees/${employee.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            contact: {
              email: formValues.email,
              phone: formattedPhoneNumber,
              address: {
                country: employer.data?.country.id ?? ""
              }
            },
            plans: selectedPlans.filter(
              (selectedPlan) => selectedPlan.tierId !== DEFAULT_VALUE
            ),
            tags
          })
        }
      );

      if (!response.ok) {
        const responseJson = await response.json();

        switch (response.status) {
          case 400:
            if (responseJson.errors["Contact.Phone"]) {
              throw new Error(responseJson.errors["Contact.Phone"][0]);
            } else if (responseJson.errors["Contact.Email"]) {
              throw new Error(responseJson.errors["Contact.Email"][0]);
            } else {
              throw new Error("There was a problem updating this user");
            }
          default:
            throw new Error("There was a problem updating this user");
        }
      }
    },
    {
      onSuccess: () => {
        toast.success("User updated successfully");
        employees.refetch();
        handleClose();
      },
      onError: (error: Error) => {
        console.error(error.message);
        setError(error.message);
      }
    }
  );

  const mutationChangeUserStatus = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `${config?.API_URL}/employers/${employerId}/employees/${employee.id}/status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            status:
              status === EmployeeStatus.Active
                ? EmployeeStatus.Inactive
                : EmployeeStatus.Active
          })
        }
      );

      if (!response.ok)
        throw new Error("There was a problem updating status for this user");
    },
    onSuccess: () => {
      toast.success("User Status updated successfully");
      employees.refetch();
      handleClose();
    },
    onError: (error: Error) => {
      console.error(error.message);
      setError(error.message);
    }
  });

  const mutationDeleteUser = useMutation({
    mutationFn: async () => {
      const response = await fetch(
        `${config?.API_URL}/employers/${employerId}/employees/${employee.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${await getToken()}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.ok)
        throw new Error("There was a problem deleting this user");
    },
    onSuccess: () => {
      toast.success("User was deleted successfully");
      employees.refetch();
      handleClose();
    },
    onError: (error: Error) => {
      console.error(error.message);
      setError(error.message);
    }
  });

  const validationSchema = useDynamicEmployeeValidationSchema(
    employer.data?.country.id ?? "",
    allEmployees.data?.items
  );

  const { resetForm, ...formik } = useFormik({
    initialValues: {
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.contact.email,
      phone: employee.contact.phone
      // address1: employee.contact.address.address1,
      // address2: employee.contact.address.address2,
      // address3: employee.contact.address.address3,
      // address4: employee.contact.address.address4,
      // address5: employee.contact.address.address5,
      // city: employee.contact.address.city,
      // stateOrProvince: employee.contact.address.stateOrProvince,
      // postalCode: employee.contact.address.postalCode,
      // country: employer.data?.country.id ?? "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      mutationUpdateUser.mutate(values);
    },
    enableReinitialize: true
  });

  // Reset form when the modal is opened
  useEffect(() => {
    if (open) {
      resetForm();
      setError("");
    }
  }, [resetForm, setError, open]);

  function updateUserStatus(event: FormEvent) {
    event.preventDefault();
    mutationChangeUserStatus.mutate();
  }

  function deleteUser(event: FormEvent) {
    event.preventDefault();
    mutationDeleteUser.mutate();
    setIsDeleteDialogOpen(false);
  }

  useEffect(() => {
    setCanUpdateStatus(
      employee.status === EmployeeStatus.Active ||
        employee.status === EmployeeStatus.Inactive
    );
  }, [employee]);

  return (
    <>
      {/* Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this user? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={deleteUser} color="secondary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth={true}>
        <form onSubmit={formik.handleSubmit}>
          <Box display="flex" justifyContent="space-between">
            <DialogTitle>Edit user</DialogTitle>
          </Box>
          <DialogContent>
            <Grid container spacing={2}>
              {error && (
                <Grid item xs={12}>
                  <Typography color="error">{error}</Typography>
                </Grid>
              )}
              <Grid container item spacing={2} xs={12} md={7}>
                <Grid container spacing={2} item xs={12}>
                  <PersonalDetailsForm form={formik} allowEmailChange={true} />
                </Grid>
                <Grid container spacing={1} item xs={12} display="block">
                  <PlanSelection
                    selectedPlans={selectedPlans}
                    setSelectedPlans={setSelectedPlans}
                    DEFAULT_VALUE={DEFAULT_VALUE}
                    disabledPlans={employee.accounts.map(
                      (account) => account.planId
                    )}
                  />
                </Grid>
                <Grid container spacing={1} item xs={12} display="flex">
                  <TagSelection tags={tags} setTags={setTags} />
                </Grid>

                <Box display="flex" flexDirection="column" gap={2} m={2} mt={5}>
                  {canUpdateStatus && (
                    <Grid
                      container
                      spacing={1}
                      item
                      xs={12}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6}>
                        <Button
                          variant="outlined"
                          color={
                            status === EmployeeStatus.Active
                              ? "error"
                              : "primary"
                          }
                          onClick={updateUserStatus}
                        >
                          {status === EmployeeStatus.Active
                            ? "Disable"
                            : "Enable"}{" "}
                          User Access
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          This will{" "}
                          {status === EmployeeStatus.Active
                            ? "remove"
                            : "enable"}{" "}
                          access to the card and HealthNow for this user
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                  {userInfoData?.employeeId !== employee.id && ( // A user cannot remove himself
                    <Grid
                      container
                      spacing={1}
                      item
                      xs={12}
                      direction="row"
                      justifyContent="space-between"
                    >
                      <Grid item xs={6}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => setIsDeleteDialogOpen(true)}
                        >
                          Delete User
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography>
                          This will permanently remove the card and user from
                          your organisation
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
              <Grid container item xs={12} md={5}>
                <Grid container item xs={12}>
                  <Card // Prioritise finding a card by status not being removed, if all are removed, then select the latest
                    cardId={
                      employee.cards.find(
                        (c) => c.status !== CardStatus.Removed
                      )?.id ??
                      employee.cards[
                        employee.cards.length > 0
                          ? employee.cards.length - 1
                          : 0
                      ]?.id
                    }
                    employee={employee}
                  />
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary">
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={mutationUpdateUser.isLoading || employer.isLoading}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
